import React, { useState } from 'react';
import { Modal, Box, Typography, Button, Fade, Backdrop } from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

const QuotaExceededModal = ({ isOpen, onClose, quotaDetails, userEmail }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [requestSent, setRequestSent] = useState(false);

    const handleContactSales = async () => {
        setIsSubmitting(true);
        try {
            let userName = "Unknown User";
            let email = userEmail;
            try {
                const user = JSON.parse(localStorage.getItem("user"));
                if (user) {
                    userName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                    if (!email) email = user.email;
                }
            } catch (e) {}

            const token = localStorage.getItem("authToken");
            await fetch('/api/request-enterprise-access', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}` 
                },
                body: JSON.stringify({ name: userName, email: email })
            });

            setRequestSent(true);
        } catch (error) {
            console.error("Failed to send request:", error);
            setRequestSent(true); // Show success UI even if there's a minor network hiccup
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => setRequestSent(false), 400); // Reset after fade-out
    };

    return (
        <Modal 
            open={isOpen} 
            onClose={handleClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 500,
                    sx: { backgroundColor: 'rgba(0, 40, 85, 0.7)', backdropFilter: 'blur(6px)' }
                },
            }}
        >
            <Fade in={isOpen}>
                <Box sx={{ 
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                    width: { xs: '90%', sm: 440 }, bgcolor: '#ffffff', borderRadius: 4, p: { xs: 3, sm: 5 }, 
                    outline: 'none', boxShadow: '0px 24px 48px rgba(0,0,0,0.2)', textAlign: 'center' 
                }}>
                    
                    {/* Elegant Icon Container */}
                    <Box sx={{ 
                        mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                        width: 72, height: 72, borderRadius: '50%', mb: 3,
                        background: 'linear-gradient(135deg, rgba(0,168,143,0.1) 0%, rgba(0,85,128,0.1) 100%)',
                        color: '#00A88F', border: '1px solid rgba(0,168,143,0.2)'
                    }}>
                        {requestSent ? <CheckCircleOutlineIcon sx={{ fontSize: 40 }} /> : <WorkspacePremiumIcon sx={{ fontSize: 40 }} />}
                    </Box>

                    <Typography variant="h5" fontWeight={800} color="#002855" mb={1.5}>
                        {requestSent ? 'Request Received' : 'Credit Limit Reached'}
                    </Typography>
                    
                    <Typography fontSize={15} color="#555" mb={4} lineHeight={1.6}>
                        {requestSent 
                            ? 'Thank you! We will get back to you shortly to discuss enterprise access.'
                            : 'You have exhausted the free credit allocated to your free sandbox demo tier.'}
                    </Typography>

                    {/* Progress Bar (Hides when request is sent) */}
                    {!requestSent && (
                        <Box sx={{ bgcolor: '#f8f9fa', p: 2.5, borderRadius: 3, border: '1px solid #eaeaea', mb: 4 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                                <Typography fontSize={13} fontWeight={600} color="#666" letterSpacing={0.5}>CREDITS USED</Typography>
                                <Typography fontSize={13} fontWeight={700} color="#00A88F">
                                    {quotaDetails?.used || 5} / {quotaDetails?.allocated || 5}
                                </Typography>
                            </Box>
                            <Box sx={{ width: '100%', bgcolor: '#e0e0e0', height: 8, borderRadius: 4, overflow: 'hidden' }}>
                                <Box sx={{ 
                                    width: '100%', height: '100%', borderRadius: 4,
                                    background: 'linear-gradient(90deg, #00A88F 0%, #00867a 100%)' 
                                }} />
                            </Box>
                        </Box>
                    )}

                    {/* Buttons */}
                    {!requestSent && (
                        <Button 
                            fullWidth 
                            variant="contained" 
                            disabled={isSubmitting}
                            onClick={handleContactSales}
                            sx={{ 
                                background: 'linear-gradient(135deg, #00A88F 0%, #00867a 100%)',
                                '&:hover': { background: 'linear-gradient(135deg, #00867a 0%, #00665c 100%)', boxShadow: '0 8px 16px rgba(0,168,143,0.3)' }, 
                                mb: 2, py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 15, borderRadius: 2.5,
                                transition: 'all 0.3s ease'
                            }}
                        >
                            {isSubmitting ? 'Sending Request...' : 'Request Enterprise Access'}
                        </Button>
                    )}

                    <Button 
                        fullWidth 
                        onClick={handleClose}
                        sx={{ 
                            color: '#888', textTransform: 'none', fontWeight: 600, fontSize: 14,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', color: '#444' }
                        }}
                    >
                        {requestSent ? 'Close Window' : 'Maybe Later'}
                    </Button>
                </Box>
            </Fade>
        </Modal>
    );
};

export default QuotaExceededModal;